import React, { useEffect, useRef, useState } from 'react';
import InputBase from './inputBase';
import { Controller, useFormContext } from 'react-hook-form';
import Button from '../button/button';
import {
  CirclePlus,
  CloudIcon,
  FwEdit,
  FwSearchIcon,
  FwTrashIcon,
  FwUploadIcon,
} from '../../icons';
import ButtonGallery from '../buttonGallery/buttonGallery';
import Modal from '../modal/modal';
import IconButton from '../iconButton/iconButton';
import { useTranslation } from 'react-i18next';

const InputMedia = ({
  id,
  name,
  placeholder,
  label,
  showDrag = true,
  className = '',
  initialValue = null,
  isRequired = false,
  maxSize = 1048576,
  idModal = null,
  titleModal = null,
  pdf = false,
  video = false,
  placeholderInputMedia = ''
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.inputMedia' });
  const validationRules = {};
  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
  } = useFormContext();
  const [modal, setModal] = useState(false);
  const [logo, setLogo] = useState(watch(name)?.base64);
  const fileInputRef = useRef(null);

  const isMobile = window.screen.width < 764;
  let acceptGeneralTypes = 'image/gif, image/png, image/jpeg, image/jpg, image/bmp';
  if (pdf) acceptGeneralTypes += ', application/pdf';
  if (video) acceptGeneralTypes += ', video/mp4';
  let allowedExtensions = /\.(gif|png|jpe?g|bmp)$/i;
  if (pdf) allowedExtensions = /\.(gif|png|jpe?g|bmp|pdf)$/i;
  if (video) allowedExtensions = /\.(gif|png|jpe?g|bmp|pdf|mp4)$/i;

  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };
  const handleRemoveFile = () => {
    fileInputRef.current.value = '';
  };
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      clearErrors(name);
      previewFile(file)
        .then((base64String) => {
          setValue(name, { base64: base64String, name: file.name });
        })
        .catch((error) => {
          console.error(t('errors.convert'), error);
        });
    }
  };
  const previewFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };
  const handleFileSelection = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (allowedExtensions.test(file.name)) {
        if (file.size <= maxSize) {
          handleFileInputChange(e);
        } else {
          setError(name, {
            type: 'custom',
            message: t('errors.size'),
          });
        }
      } else {
        setError(name, {
          type: 'custom',
          message: t('errors.type'),
        });
      }
    }
  };
  const toggleModal = (open = false) => {
    setModal(open);
  };
  const deleteImage = () => {
    setValue(name, '');
    setLogo(null);
  };
  const renderPreview = (value) => {
    if (!value || !value.base64) {
      return null;
    }

    if (value?.name?.endsWith('.mp4')) {
      return (
        <video width='100%' height='100%' controls>
          <source src={value.base64} type='video/mp4' />
          {t('notSupported')}
        </video>
      );
    }

    return <img src={value.base64} alt={value.name} className='preview-image' />;
  };

  if (isRequired) {
    validationRules.required = t('errors.general');
  }
  useEffect(() => {
    clearErrors();
  }, [isRequired]);

  return (
    <div className={`input-file-container ${className}`}>
      {label && (
        <label className='mb-1' style={{ color: 'var(--color-text)' }}>
          {label}
        </label>
      )}
      <Controller
        control={control}
        name={name}
        rules={validationRules}
        render={({ field: { value, onChange } }) => {
          return (
            <>
              <Modal
                textCancel={t('cancel')}
                textSubmit={t('save')}
                idModal={idModal}
                title={titleModal}
                onClose={() => toggleModal(false)}
                open={modal}
                handleSubmit={() => {
                  watch(name)?.base64 ? setLogo(watch(name).base64) : setLogo(null);
                  toggleModal(false);
                }}
              >
                <div className='p-2'>
                  {showDrag ? (
                    <div
                      className='file-dropzone mb-2'
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const droppedFile = e.dataTransfer.files[0];
                        if (droppedFile) {
                          if (allowedExtensions.test(droppedFile.name)) {
                            if (droppedFile.size <= maxSize) {
                              clearErrors(name);
                              previewFile(droppedFile)
                                .then((base64String) => {
                                  setValue(name, { base64: base64String, name: droppedFile.name });
                                })
                                .catch((error) => {
                                  console.error(t('errors.convert'), error);
                                });
                            } else {
                              setError(name, {
                                type: 'custom',
                                message: t('errors.size'),
                              });
                            }
                          } else {
                            setError(name, {
                              type: 'custom',
                              message: t('errors.type'),
                            });
                          }
                        }
                      }}
                    >
                      {value && value?.base64 ? (
                        value?.name?.endsWith('.mp4') ? (
                          <video width='100%' height='100%' controls>
                            <source src={value.base64} type='video/mp4' />
                            {t('notSupported')}
                          </video>
                        ) : (
                          <img src={value.base64} alt={value.name} className='preview-image' />
                        )
                      ) : (
                        <p>{isMobile ? t('uploadText.mobile') : t('uploadText.desktop')}</p>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                  <input
                    type='file'
                    id={id}
                    name={name}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelection}
                    accept={acceptGeneralTypes}
                  />
                  <InputBase
                    errors={errors}
                    icon={<FwUploadIcon />}
                    type='text'
                    name={name}
                    value={value?.name || ''}
                    label={''}
                    readOnly={true}
                    placeholder={placeholder}
                    disabled
                    endComponent={
                      <div className='file-btn d-flex'>
                        {value ? (
                          <>
                            <Button
                              type='button'
                              size={'medium'}
                              className={'remove-file'}
                              heights={'44px'}
                              text={<FwTrashIcon />}
                              onClick={() => {
                                handleRemoveFile();
                                deleteImage();
                                onChange(null);
                              }}
                            />
                          </>
                        ) : (
                          <></>
                        )}
                        <Button
                          type='button'
                          size={'medium'}
                          className={'send-file'}
                          heights={'44px'}
                          text={<FwSearchIcon color='' />}
                          onClick={handleFileInputClick}
                        />
                      </div>
                    }
                  />
                  {errors[name] && (
                    <div className='d-flex align-items-center error-content'>
                      <i className='fas fa-exclamation-triangle text-danger mr-2'></i>
                      <p className='text-danger p-0 m-0'>{errors[name].message}</p>
                    </div>
                  )}
                </div>
              </Modal>
              {logo ? (
                <div className='w-100 p-0 gallery-item'>
                  <div className='card-gallery'>
                    <div className='card-img-actions p-1'>
                      <div className='image-container' style={{ height: '100%' }}>
                        {renderPreview({ base64: logo, name: watch(name)?.name })}
                      </div>
                    </div>
                    <div className='image-overlay d-flex justify-content-end'>
                      <IconButton
                        onClick={() => toggleModal(true)}
                        classNameIcon={'hoverIconConatiner'}
                        icon={<FwEdit color='var(--hover)' size={20} classNameIcon={'hoverIcon'} />}
                      />
                      <IconButton
                        onClick={deleteImage}
                        classNameIcon={'hoverIconConatiner'}
                        icon={
                          <FwTrashIcon color='var(--hover)' size={20} classNameIcon={'hoverIcon'} />
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <ButtonGallery
                  icon={<CloudIcon size='20px' color='var(--color-text-light)' />}
                  text={t('uploadText.mobile')}
                  height='150px'
                  onClick={() => toggleModal(true)}
                  width='100%'
                  msg={placeholderInputMedia ? placeholderInputMedia : t('text')}
                  stylesError={errors[name]}
                />
              )}
              {errors[name] && (
                <div className='d-flex align-items-center error-content'>
                  <i className='fas fa-exclamation-triangle text-danger mr-2'></i>
                  <p className='text-danger p-0 m-0'>{errors[name].message}</p>
                </div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};
export default InputMedia;
